import { Injectable } from '@angular/core';
import { BookingLocationPlace } from '../models/booking.model';
import { ServiceId } from '../models/service.model';

const DRAFT_KEY = 'seamus.bookingDraft';
const RETURN_URL_KEY = 'seamus.authReturnUrl';
const PENDING_ID_KEY = 'seamus.pendingBookingId';

export interface BookingDraft {
  serviceId: ServiceId;
  date: string;
  startTime: string;
  endTime: string;
  blockStartTime: string;
  blockEndTime: string;
  notes: string;
  locationPlace: BookingLocationPlace;
  locationAddress: string;
  quantity: number;
  /** Neighbor name typed on confirm (passwordless / pre-auth). */
  customerName?: string;
  /** Email used for the magic link (informational; Auth is source of truth after sign-in). */
  customerEmail?: string;
  /** After login, finish the booking without asking again. */
  autoConfirm: boolean;
}

@Injectable({ providedIn: 'root' })
export class BookingDraftService {
  /** Persist draft in localStorage so email clients opening a new tab can still finish booking. */
  save(draft: BookingDraft): void {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (err) {
      console.warn('Could not save booking draft', err);
    }
  }

  peek(): BookingDraft | null {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as BookingDraft;
      if (!parsed?.serviceId || !parsed?.date || !parsed?.startTime) return null;
      return {
        ...parsed,
        locationPlace: parsed.locationPlace === 'provider' ? 'provider' : 'customer',
        locationAddress: parsed.locationAddress ?? '',
        quantity: Math.max(1, Math.floor(Number(parsed.quantity) || 1)),
      };
    } catch {
      return null;
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Take the draft once (clears storage). */
  consume(): BookingDraft | null {
    const draft = this.peek();
    if (draft) this.clear();
    return draft;
  }

  savePendingId(pendingId: string): void {
    try {
      localStorage.setItem(PENDING_ID_KEY, pendingId);
    } catch {
      /* ignore */
    }
  }

  peekPendingId(): string | null {
    try {
      return localStorage.getItem(PENDING_ID_KEY);
    } catch {
      return null;
    }
  }

  clearPendingId(): void {
    try {
      localStorage.removeItem(PENDING_ID_KEY);
    } catch {
      /* ignore */
    }
  }

  saveReturnUrl(url: string): void {
    try {
      localStorage.setItem(RETURN_URL_KEY, url);
    } catch {
      /* ignore */
    }
  }

  consumeReturnUrl(fallback = '/book'): string {
    try {
      const url = localStorage.getItem(RETURN_URL_KEY);
      localStorage.removeItem(RETURN_URL_KEY);
      if (url && url.startsWith('/') && !url.startsWith('//')) return url;
    } catch {
      /* ignore */
    }
    return fallback;
  }
}
